import { useMutation, useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import type { RequestParams, ApiResponse } from '@/shared/api';
import type { ApiResponseList } from '@/shared/api/types';
import { AGENT_KEYS } from '../lib';
import { AgentApi } from '../api';
import type {
  AgentBase,
  AgentCreatePayload,
  AgentUpdatePayload,
  AgentItem,
  AgentItemListParams,
  AgentProspectsResult,
  AgentProspectsParams,
  AgentProspectVisitTask,
  AgentProspectVisitTaskListParams,
} from './types';

export const useAgentItemList = (params?: AgentItemListParams) => {
  const hasNameFilter = Boolean(params?.name && params.name.trim().length >= 1);
  const hasIdsFilter = Boolean(params?.ids && params.ids.length > 0);

  return useQuery<ApiResponse<AgentItem[]>, Error, AgentItem[] | undefined>({
    queryKey: AGENT_KEYS.itemList(params),
    queryFn: () => AgentApi.getItemList(params),
    enabled: hasNameFilter || hasIdsFilter,
    placeholderData: keepPreviousData,
    select: (data) => data.result ?? [],
  });
};

export const useAgentList = (params?: RequestParams) => {
  return useQuery<ApiResponseList<AgentBase>>({
    queryKey: AGENT_KEYS.list(params),
    queryFn: () => AgentApi.getList(params),
    placeholderData: keepPreviousData,
  });
};

export const useAgentById = (id: number) => {
  return useQuery<ApiResponse<AgentBase>>({
    queryKey: AGENT_KEYS.detail(id),
    queryFn: () => AgentApi.getById(id),
    enabled: !!id,
  });
};

export const useAgentProspects = (params: AgentProspectsParams) => {
  return useQuery<ApiResponse<AgentProspectsResult>>({
    queryKey: AGENT_KEYS.prospects(params),
    queryFn: () => AgentApi.getProspects(params),
    enabled: !!params.id,
    placeholderData: keepPreviousData,
  });
};

export const useAgentProspectVisitTasks = (
  params: AgentProspectVisitTaskListParams,
  enabled = true
) => {
  return useQuery<ApiResponseList<AgentProspectVisitTask>>({
    queryKey: AGENT_KEYS.prospectVisitTasks(params),
    queryFn: () => AgentApi.getProspectVisitTasks(params),
    enabled: enabled && !!params.agent_id && !!params.prospect_id,
    placeholderData: keepPreviousData,
  });
};

export const useCreateAgent = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: AgentCreatePayload) => AgentApi.create(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: AGENT_KEYS.all });
    },
  });
};

export const useUpdateAgent = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: AgentUpdatePayload) => AgentApi.update(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: AGENT_KEYS.all });
    },
  });
};

export const useDeleteAgent = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => AgentApi.remove(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: AGENT_KEYS.all });
    },
  });
};
